//eslint-disable-next-line no-unused-vars
import {
  Box,
  Button,
  Checkbox,
  Typography,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  Skeleton,
} from "@mui/material";
import { memo, useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { formatProduct, formatPriceWithPrecision } from "../../../utils/utilfunction";
import { exchangeSpecificStringifiedNumber } from "../../../services/exchanges";
import { stringifyNumberToIndianLocale } from "../../../services/utilities";
import ConfirmationModal from "../../Common/ConfimationModel";
import MobileView from "./MobileView";
import PendingOrdersModal from "../../Common/PendingOrdersModal";
import GenericTable from "../../Common/GenericTable";
import { positionsConfig } from "../../../config/tableConfigs";
import { usePositions } from "../../../hooks/usePositions";
import PositionDetailsDrawer from './PositionDetailsDrawer';
import CommonMessages from '../../Common/NoDataMessages';
import NoOrdersIllustration from '../../Common/NoOrdersIllustration';
import useBalance from "./useBalance";
import { fetchFundsView } from "../../../store/actions/funds";
import { fetchPositionsAction } from "../../../store/actions/portfolio";
import { fetchPendingOrdersAction } from "../../../store/actions/orders";


function Positions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedPositionForDetails, setSelectedPositionForDetails] = useState(null);
  const [conversionMode, setConversionMode] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [error, setError] = useState("");
  const [pnlDetailsOpen, setPnlDetailsOpen] = useState(false);

  // Use the balance hook to get consistent PnL values across the app
  const balanceData = useBalance();

  const {
    // total,
    totalInvestedValue,
    totalPnlPercentage,
    isAllSelected,
    selectedPosition,
    openConfirmationBox,
    positions,
    isFetchingPositions,
    conversionDrawerOpen,
    selectedPositionForConversion,
    pendingOrdersModalOpen,
    conflictingOrders,
    cancellingOrders,
    handleCheckbox,
    handleSquareOffClick,
    setOpenConfirmationBox,
    executeSquareOff,
    handleSelectAllTap,
    selectAllHandlers,
    selectAllTouchStates,
    handleConvertPosition,
    handleCloseConversionDrawer,
    handleAddMore,
    handleCancelPendingOrder,
    handleClosePendingOrdersModal,
    getPositionPendingOrders,
    getSelectedPositionsWithPendingOrders,
    handleSquareOffSingle,
    dispatch,
  } = usePositions();

  // Fetch funds data when component mounts
  // useEffect(() => {
  //   dispatch(fetchFundsView());
  //   dispatch(fetchPositionsAction("historical"));
  // }, [dispatch]);
  
  const positionMetrics = useSelector(state => state.portfolio.positionMetrics || {});
  console.log("positionMetrics", positionMetrics);

  // Always filter out positions with zero quantity
  const filteredPositions = positions?.filter(position => position.net_quantity !== 0);
  const filteredPositionsTotalMarketVal = filteredPositions?.reduce((acc, curr) => acc + (curr.avgPrice * Math.abs(curr.net_quantity)), 0);
  console.log("filteredPositions>", filteredPositions)

  // Function to determine target product type
  const getTargetProductType = (currentProduct, exchange) => {
    if (!currentProduct || !exchange) {
      return null;
    }

    if (currentProduct === 'MIS') {
      return exchange === 'NSE' || exchange === 'BSE' ? 'CNC' : 'NRML';
    } else {
      return 'MIS';
    }
  };

  // Handle row click to open details drawer
  const handleRowClick = useCallback((position) => {
    setSelectedPositionForDetails(position);
    setConversionMode(false);
    setQuantity(String(Math.abs(position.net_quantity)));
    setError("");
  }, []);

  // Close details drawer
  const handleCloseDetailsDrawer = useCallback(() => {
    setSelectedPositionForDetails(null);
    setConversionMode(false);
  }, []);

  // Handle quantity change
  const handleQuantityChange = useCallback((e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setQuantity(value);
      setError("");

      if (selectedPositionForDetails && Number(value) > Math.abs(selectedPositionForDetails.net_quantity)) {
        setError("Quantity cannot exceed position size");
      }
    }
  }, [selectedPositionForDetails]);

  // Handle convert
  const handleConvert = useCallback(() => {
    if (!selectedPositionForDetails) {
      setError("Position data is missing");
      return;
    }

    if (!quantity) {
      setError("Please enter a quantity");
      return;
    }

    if (Number(quantity) > Math.abs(selectedPositionForDetails.net_quantity)) {
      setError("Quantity cannot exceed position size");
      return;
    }

    const targetProduct = getTargetProductType(selectedPositionForDetails.product, selectedPositionForDetails.exchange);

    // Execute conversion
    handleConvertPosition(selectedPositionForDetails, Number(quantity), targetProduct);

    // Close drawer after conversion
    setSelectedPositionForDetails(null);
    setConversionMode(false);
  }, [selectedPositionForDetails, quantity, handleConvertPosition]);

  // Add touch handling to prevent parent scroll
  const handleTouchMove = (event) => {
    const element = event.currentTarget;
    const isAtTop = element.scrollTop === 0;
    const isAtBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    if (
      (isAtTop && event.touches[0].clientY > 0) ||
      (isAtBottom && event.touches[0].clientY < 0)
    ) {
      event.preventDefault();
    }
  };

  // Toggle PnL details visibility
  const togglePnlDetails = () => {
    setPnlDetailsOpen(!pnlDetailsOpen);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Compact header: Row 1 = label + controls, Row 2 = P&L value */}
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          px: { xs: 1.5, sm: 2 },
          py: 0.75,
        }}
      >
        {/* P&L Group */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: !isMobile ? "flex-end" : "stretch" }}>
          {/* Row 1: Label + selection controls (Mobile only) */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: !isMobile ? "flex-end" : "space-between", width: "100%" }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", fontSize: "0.7rem" }}>
              Unrealized P&L
            </Typography>

            {isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                {/* Cross-fade slot: Select All text ↔ Square Off button */}
                <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    onClick={selectedPosition.length === 0 ? handleSelectAllTap : undefined}
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.7rem",
                      whiteSpace: "nowrap",
                      pointerEvents: selectedPosition.length === 0 ? "auto" : "none",
                      cursor: selectedPosition.length === 0 ? "pointer" : "default",
                      opacity: selectedPosition.length === 0 ? 1 : 0,
                      transition: "opacity 0.2s ease",
                      position: selectedPosition.length > 0 ? "absolute" : "relative",
                      right: 0,
                    }}
                  >
                    Select All
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSquareOffClick}
                    sx={{
                      minWidth: "auto",
                      px: 1,
                      py: 0.125,
                      height: 22,
                      textTransform: "none",
                      fontSize: "0.625rem",
                      whiteSpace: "nowrap",
                      WebkitTapHighlightColor: "transparent",
                      opacity: selectedPosition.length > 0 ? 1 : 0,
                      pointerEvents: selectedPosition.length > 0 ? "auto" : "none",
                      transition: "opacity 0.2s ease",
                      position: selectedPosition.length === 0 ? "absolute" : "relative",
                      right: 0,
                      "&:active": { transform: "scale(0.98)" },
                    }}
                  >
                    Square Off ({selectedPosition.length})
                  </Button>
                </Box>
                <Box
                  {...selectAllHandlers}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    borderRadius: 1,
                    p: 0.25,
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    name="allSelect"
                    size="small"
                    sx={{
                      p: 0.25,
                      pointerEvents: "none",
                      "& .MuiSvgIcon-root": { fontSize: "1.1rem" },
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Row 2: P&L value */}
          {isFetchingPositions || filteredPositions.some(p => p.isLtpStale) ? (
            <Skeleton animation="wave" width={130} height={20} sx={{ mt: 0.25, ml: !isMobile ? "auto" : 0 }} />
          ) : (
            <Typography
              sx={{
                color: balanceData.isPositive.totalPnl ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 700,
                whiteSpace: "nowrap",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: !isMobile ? "right" : "left",
              }}
            >
              {`${balanceData.isPositive.totalPnl ? '+' : ''}₹${stringifyNumberToIndianLocale(Math.abs(balanceData.totalPnl), 2)}`}
              <Box component="span" sx={{ fontSize: "0.7rem", ml: 0.5 }}>
                {`(${filteredPositions.length > 0 ? stringifyNumberToIndianLocale(Math.abs((balanceData.totalPnl / filteredPositionsTotalMarketVal) * 100), 2) : 0}%)`}
              </Box>
            </Typography>
          )}
        </Box>

        {/* Desktop Select All Controls */}
        {!isMobile && (
          <Box sx={{ 
            position: "absolute", 
            left: '16px', 
            top: "50%", 
            transform: "translateY(-50%)", 
            display: "flex", 
            alignItems: "center" 
          }}>
            <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
              <Typography
                variant="body2"
                onClick={selectedPosition.length === 0 ? handleSelectAllTap : undefined}
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  pointerEvents: selectedPosition.length === 0 ? "auto" : "none",
                  cursor: selectedPosition.length === 0 ? "pointer" : "default",
                  opacity: selectedPosition.length === 0 ? 1 : 0,
                  transition: "all 0.2s ease",
                  position: selectedPosition.length > 0 ? "absolute" : "relative",
                  left: 0,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: selectedPosition.length === 0 ? theme.palette.action.hover : "transparent",
                  },
                  "&:active": {
                    transform: selectedPosition.length === 0 ? "scale(0.98)" : "none",
                  }
                }}
              >
                Select All
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSquareOffClick}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.125,
                  height: 22,
                  textTransform: "none",
                  fontSize: "0.625rem",
                  whiteSpace: "nowrap",
                  WebkitTapHighlightColor: "transparent",
                  opacity: selectedPosition.length > 0 ? 1 : 0,
                  pointerEvents: selectedPosition.length > 0 ? "auto" : "none",
                  transition: "opacity 0.2s ease",
                  position: selectedPosition.length === 0 ? "absolute" : "relative",
                  left: 0,
                  "&:active": { transform: "scale(0.98)" },
                }}
              >
                Square Off ({selectedPosition.length})
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {filteredPositions.length === 0 ? (
        <CommonMessages
          icon={<NoOrdersIllustration />}
          messageHead="No positions"
          messageBody="You have no open positions at the moment"
        />
      ) : isMobile ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: "relative",
            overflow: "hidden",
            touchAction: "none",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Box
            onTouchMove={handleTouchMove}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              overscrollBehavior: "contain",
              touchAction: "pan-y",
              backgroundColor: theme.palette.background.default,
            }}
          >
            <MobileView
              positions={filteredPositions}
              selectedPosition={selectedPosition}
              handleCheckbox={handleCheckbox}
              formatProduct={formatProduct}
              exchangeSpecificStringifiedNumber={exchangeSpecificStringifiedNumber}
              onRowClick={handleRowClick}
              getPositionPendingOrders={getPositionPendingOrders}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          <GenericTable
            data={filteredPositions}
            config={positionsConfig(isMobile, exchangeSpecificStringifiedNumber)}
            selectedItems={selectedPosition}
            handleCheckbox={handleCheckbox}
            showCheckbox={true}
            isAllSelected={isAllSelected}
            isMobile={isMobile}
            onRowClick={handleRowClick}
          />
        </Box>
      )}

      <ConfirmationModal
        open={openConfirmationBox}
        setOpen={setOpenConfirmationBox}
        heading="Are you sure?"
        message="All selected positions will be squared off at market price"
        callFunc={executeSquareOff}
      />

      <PendingOrdersModal
        open={pendingOrdersModalOpen}
        onClose={handleClosePendingOrdersModal}
        pendingOrders={conflictingOrders}
        onCancelOrder={handleCancelPendingOrder}
        cancellingOrders={cancellingOrders}
        title="Square-off Blocked"
        message="Please cancel the following pending orders before proceeding with square-off:"
        onProceed={() => {
          handleClosePendingOrdersModal();
          if (selectedPosition.length === 1) {
            executeSquareOff();
          } else {
            setOpenConfirmationBox(true);
          }
        }}
      />

      <PositionDetailsDrawer
        open={!!selectedPositionForDetails}
        onClose={handleCloseDetailsDrawer}
        position={selectedPositionForDetails}
        conversionMode={conversionMode}
        setConversionMode={setConversionMode}
        quantity={quantity}
        error={error}
        handleQuantityChange={handleQuantityChange}
        handleConvert={handleConvert}
        getTargetProductType={getTargetProductType}
        onAddMore={handleAddMore}
        onSquareOff={handleSquareOffSingle}
      />

    </Box>
  );
}

export default memo(Positions);
